import React, { useState } from "react";
import { Field, connect } from "formik";
import cx from "classnames";
import { useTranslation } from "react-i18next";

const EyeOpen = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeClosed = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const C = { red: "#ef4444", yellow: "#f59e0b", green: "#16a34a" };
const ICO = { red: "✗", yellow: "●", green: "✓" };

const getRules = (p) => [
  { label: "Минимум 8 символов", status: p.length === 0 ? "red" : p.length >= 8 ? "green" : "yellow" },
  { label: "Заглавная буква (A-Z)", status: !p ? "red" : /[A-Z]/.test(p) ? "green" : "yellow" },
  { label: "Цифра (0-9)", status: !p ? "red" : /[0-9]/.test(p) ? "green" : "yellow" },
  { label: "Специальный символ (!@#$...)", status: !p ? "red" : /[^a-zA-Z0-9]/.test(p) ? "green" : "yellow" },
];

const validatePassword = (value, optional = false) => {
  if (!value) return optional ? undefined : "Обязательное поле";
  if (value.length < 8) return "Минимум 8 символов";
  if (!/[A-Z]/.test(value)) return "Нужна заглавная буква (A-Z)";
  if (!/[0-9]/.test(value)) return "Нужна цифра (0-9)";
  if (/^[a-zA-Z0-9]*$/.test(value)) return "Нужен специальный символ";
  return undefined;
};

const PasswordInput = ({
  className,
  label,
  size,
  field,
  disabled,
  isRequired,
  placeholder,
  showStrength,
  form: { touched, errors },
}) => {
  const { t } = useTranslation("main");
  const [show, setShow] = useState(false);
  const p = field.value || "";
  const rules = getRules(p);

  const classes = cx(
    "form-field",
    touched[field.name] && errors[field.name] && "form-field--error",
    `form-field--${size || "default"}`,
    disabled && "form-field--disabled",
    field.value && "form-field--active",
    className
  );

  return (
    <div className={classes}>
      {label && (
        <label className="form-field__label">
          {label}
          {isRequired && <span title="Обязательный поля">*</span>}
        </label>
      )}
      <div className="input-group">
        <input
          className="form-field__input"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          type={show ? "text" : "password"}
          style={{ paddingRight: 36 }}
          {...field}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#6b7280",
            padding: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {show ? <EyeOpen /> : <EyeClosed />}
        </button>
        {touched[field.name] && errors[field.name] && (
          <div className="form-field__error" />
        )}
      </div>
      {touched[field.name] && errors[field.name] && (
        <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>
          {errors[field.name]}
        </div>
      )}
      {showStrength !== false && (
        <div style={{ marginTop: 4, marginBottom: 8 }}>
          {rules.map((rule) => (
            <div key={rule.label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
              <span style={{ color: C[rule.status], fontWeight: 700, fontSize: 13, width: 14, textAlign: "center", lineHeight: 1 }}>
                {ICO[rule.status]}
              </span>
              <span style={{ fontSize: 12, color: C[rule.status] }}>{rule.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// connect() orqali formik.values ga ega bo'lamiz (Formik v1)
const PasswordFieldInner = ({
  name = "password",
  withConfirm = false,
  confirmName,
  confirmLabel,
  showStrength,
  size,
  isRequired,
  formik,
  ...props
}) => {
  const confName = confirmName || `${name}_confirmation`;
  const optional = !isRequired;

  const mainValidate = (v) => {
    if (!v && optional) return undefined;
    return validatePassword(v);
  };

  const confirmValidate = (value) => {
    const main = formik.values[name];
    if (!value && !main && optional) return undefined;
    if (!value) return "Подтвердите пароль";
    if (value !== main) return "Пароли не совпадают";
    return undefined;
  };

  return (
    <>
      <Field
        component={PasswordInput}
        name={name}
        validate={mainValidate}
        showStrength={showStrength}
        size={size}
        isRequired={isRequired}
        {...props}
      />
      {withConfirm && (
        <Field
          component={PasswordInput}
          name={confName}
          validate={confirmValidate}
          label={confirmLabel || "Подтвердите пароль"}
          placeholder="Повторите пароль"
          isRequired={isRequired}
          size={size}
          showStrength={false}
        />
      )}
    </>
  );
};

/**
 * Ishlatish:
 *   <Fields.PasswordField name="password" label="Пароль" isRequired />
 *
 * Tasdiqlash bilan:
 *   <Fields.PasswordField name="password" label="Пароль" isRequired withConfirm />
 */
const PasswordField = connect(PasswordFieldInner);

export default PasswordField;
